// WanPriorityPanel — direct-mode WAN priority editor.
//
// Direct edges pick the active WAN by walking an ordered preference list.
// This panel reads it from GET /direct/priority and lets the operator
// reorder, add, and remove entries before PUT'ing the new list back.
//
// Only mounted when the selected node's mode === "direct".
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useWanName, WanLabel } from "./WanNames";
import { useToast } from "./ToastProvider";
import useSortable from "../hooks/useSortable";

export default function WanPriorityPanel({ nodeApiUrl, selectedNode }) {
  const wanName = useWanName();
  const toast = useToast();
  const [priority, setPriority] = useState([]);
  const [available, setAvailable] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Load priority + WAN inventory + status snapshot.
  const refresh = useCallback(async () => {
    if (!selectedNode) return;
    setLoading(true);
    try {
      const [pRes, wRes, sRes] = await Promise.all([
        fetch(nodeApiUrl("/direct/priority")),
        fetch(nodeApiUrl("/wans")),
        fetch(nodeApiUrl("/direct/status")),
      ]);
      if (pRes.ok) {
        const pb = await pRes.json();
        setPriority(Array.isArray(pb.priority) ? pb.priority : []);
      }
      if (wRes.ok) {
        const wb = await wRes.json();
        const list = Array.isArray(wb) ? wb : wb.wans || [];
        setAvailable(list.map((w) => w.name).filter(Boolean));
      }
      if (sRes.ok) setStatus(await sRes.json());
      setDirty(false);
    } catch (err) {
      toast?.error?.("Failed to load WAN priority: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedNode, nodeApiUrl, toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const removed = useMemo(
    () => available.filter((n) => !priority.includes(n)),
    [available, priority],
  );

  const moveTo = (from, to) => {
    if (from === to || from < 0 || to < 0) return;
    setPriority((prev) => {
      const next = prev.slice();
      const [it] = next.splice(from, 1);
      next.splice(to, 0, it);
      return next;
    });
    setDirty(true);
  };
  const sort = useSortable(moveTo);

  const remove = (idx) => {
    setPriority((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const add = (name) => {
    setPriority((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setDirty(true);
  };

  const save = async () => {
    if (priority.length === 0) {
      toast?.error?.("Priority list cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(nodeApiUrl("/direct/priority"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priority }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const body = await res.json();
      setPriority(body.priority || priority);
      setDirty(false);
      toast?.success?.("WAN priority updated");
      // Refresh status so the "active" badge reflects the new pick.
      const sRes = await fetch(nodeApiUrl("/direct/status"));
      if (sRes.ok) setStatus(await sRes.json());
    } catch (err) {
      toast?.error?.("Save failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!selectedNode) {
    return <div className="p-6 text-sm text-zinc-400">Select an edge node.</div>;
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-zinc-100">WAN Priority</h2>
        <p className="text-sm text-zinc-400 mt-1">
          Ordered preference list. The edge picks the highest-priority WAN that
          is up and not degraded.{" "}
          {status?.sticky && (
            <span className="text-amber-400">
              Sticky failback is on — the edge only switches when the active
              link itself degrades.
            </span>
          )}
        </p>
      </div>

      {status && (
        <div className="mb-4 flex items-center gap-3 text-sm text-zinc-300">
          <span className="px-2 py-0.5 rounded bg-zinc-800 border border-zinc-700">
            Active: <strong className="text-zinc-100">{status.active || "—"}</strong>
          </span>
          <span className="px-2 py-0.5 rounded bg-zinc-800 border border-zinc-700">
            Firewall: {status.firewall || "—"}
          </span>
          <span className="px-2 py-0.5 rounded bg-zinc-800 border border-zinc-700">
            Failback hold: {status.failback_hold || "—"}
          </span>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-400">Loading…</div>
      ) : (
        <>
          <ol className={`space-y-2 mb-4${sort.dragging !== null ? " select-none" : ""}`} ref={sort.listRef}>
            {priority.map((name, idx) => (
              <li
                key={name}
                {...sort.rowProps(idx)}
                className={`relative flex items-center gap-3 px-3 py-2 rounded bg-zinc-900 border hover:border-zinc-500 cursor-grab ${
                  sort.dragging === idx ? "z-10 border-zinc-400 shadow-lg cursor-grabbing" : "border-zinc-700 transition-transform"
                }`}
              >
                <span data-sort-handle style={{ touchAction: "none" }} className="text-zinc-500 font-mono text-xs w-6">{idx + 1}.</span>
                <span className="flex-1 text-zinc-100"><WanLabel iface={name} /></span>
                {status?.active === name && (
                  <span className="text-xs text-emerald-400">ACTIVE</span>
                )}
                <div className="flex gap-1">
                  <button
                    onClick={() => moveTo(idx, idx - 1)}
                    disabled={idx === 0}
                    className="px-2 py-0.5 text-xs rounded border border-zinc-700 text-zinc-300 disabled:opacity-30"
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => moveTo(idx, idx + 1)}
                    disabled={idx === priority.length - 1}
                    className="px-2 py-0.5 text-xs rounded border border-zinc-700 text-zinc-300 disabled:opacity-30"
                    title="Move down"
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => remove(idx)}
                    className="px-2 py-0.5 text-xs rounded border border-red-900 text-red-300 hover:bg-red-950"
                    title="Remove from priority list"
                  >
                    ✕
                  </button>
                </div>
              </li>
            ))}
            {priority.length === 0 && (
              <li className="text-sm text-zinc-500 italic">No WANs in priority list.</li>
            )}
          </ol>

          {removed.length > 0 && (
            <div className="mb-4">
              <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">
                Available
              </div>
              <div className="flex flex-wrap gap-2">
                {removed.map((name) => (
                  <button
                    key={name}
                    onClick={() => add(name)}
                    className="px-2 py-1 text-sm rounded border border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-emerald-700"
                  >
                    + {wanName(name)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={!dirty || saving || priority.length === 0}
              className="px-4 py-2 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? "Saving…" : dirty ? "Save priority" : "Saved"}
            </button>
            <button
              onClick={refresh}
              disabled={saving}
              className="px-4 py-2 rounded border border-zinc-700 text-zinc-300 hover:border-zinc-500 text-sm"
            >
              Reload
            </button>
          </div>
        </>
      )}
    </div>
  );
}
